import React, { useMemo } from "react";
import Konva from "konva";
import { Tag } from "shared/datamodel/schemas/metadata";
import { useTaskTags } from "../hooks/use-task-tags";
import { ITaskController } from "../../controller";
import useObservableController from "elements/hooks/use-observable-controller";
import { TaskTag } from "./task-tag";
import { TAG_GAP, TAG_MAX_WIDTH, TAG_PADDING, TAG_ROW_WIDTH } from "../../consts";

/**
 * Displays tags on the task element. Render only when:
 * - A tag has been updated on the canvas (useTaskTags)
 * - A tag has been changed on the task (Subscribed to "tag-changed")
 * - Tooltip state changes
 */
export const TaskElementTags = React.memo(({ controller }: { controller: ITaskController }) => {
  useObservableController(controller, "tag-changed");

  const tags = useTaskTags(controller, 5); // Limit to 5 tags for display, actual limit depends on size

  const tagPositions = useMemo(() => {
    const MAX_TEXT_WIDTH = TAG_MAX_WIDTH - 2 * TAG_PADDING;

    let currentX = 0;
    const result: { tag: Tag; tagX: number; tagWidth: number; textWidth: number; isTruncated: boolean }[] = [];

    for (const tag of tags) {
      const textNode = new Konva.Text({
        text: tag.name,
        fontSize: 12,
        fontWeight: 400,
        fontFamily: "Poppins",
        letterSpacing: 0.124,
      });

      const actualTextWidth = textNode.getTextWidth();
      const textWidth = Math.min(actualTextWidth, MAX_TEXT_WIDTH);
      const isTruncated = actualTextWidth > textWidth;
      const tagWidth = Math.min(TAG_MAX_WIDTH, textWidth + 2 * TAG_PADDING);

      if (currentX + tagWidth > TAG_ROW_WIDTH) {
        break;
      }

      result.push({ tag, tagX: currentX, tagWidth, textWidth, isTruncated });
      currentX += tagWidth + TAG_GAP;
    }

    return result;
  }, [tags]);

  return (
    <>
      {tagPositions.map(({ tag, tagX, tagWidth, textWidth, isTruncated }, index: number) => (
        <TaskTag
          key={index}
          tag={tag}
          tagX={tagX}
          tagWidth={tagWidth}
          textWidth={textWidth}
          isTruncated={isTruncated}
        />
      ))}
    </>
  );
});

TaskElementTags.displayName = "TaskElementTags";
export default TaskElementTags;
